import React, { useState } from "react";
import {
  Badge,
  Box,
  Link,
  SpaceBetween,
  Table,
  TableProps,
  TextFilter,
} from "@amzn/awsui-components-react";

import { AsinWithCatalogIssues } from "../../../from-server/asin-central/catalog-issues";
import { selectionCentralUrlForObfuscatedMarketplace } from "../../../util/selectionCentralUrl";
import { pluralize } from "../../../util/pluralize";

export interface CatalogIssuesTableProps {
  /** Rows to list (already client-side filtered by the parent). */
  readonly items: readonly AsinWithCatalogIssues[];
  readonly loading: boolean;
  /** Current client-side filter text (owned by the parent). */
  readonly filteringText: string;
  readonly onFilteringChange: (filteringText: string) => void;
  /** Checked rows (controlled) — selecting a row reveals its Issue Details. */
  readonly selectedItems: readonly AsinWithCatalogIssues[];
  readonly onSelectionChange: (items: AsinWithCatalogIssues[]) => void;
}

/**
 * Error / warning counts as coloured badge pills. A pill is omitted when
 * its count is zero.
 */
export const IssueCountBadges: React.FC<{
  readonly item: AsinWithCatalogIssues;
}> = ({ item }) => {
  return (
    <SpaceBetween direction="horizontal" size="xs">
      {item.errorCount > 0 && (
        <Badge color="red">{pluralize(item.errorCount, "error")}</Badge>
      )}
      {item.warningCount > 0 && (
        <Badge color="severity-medium">
          {pluralize(item.warningCount, "warning")}
        </Badge>
      )}
    </SpaceBetween>
  );
};

/**
 * ASIN cell: deep links to Selection Central, which is where a user goes to
 * actually remediate a catalog issue. Falls back to plain text when the
 * obfuscated marketplace id cannot be decrypted, so a bad id degrades to an
 * unlinked ASIN instead of a broken link.
 */
export const AsinSelectionCentralCell: React.FC<{
  readonly item: AsinWithCatalogIssues;
}> = ({ item }) => {
  const href = selectionCentralUrlForObfuscatedMarketplace(
    item.asin,
    item.obfuscatedMarketplaceId
  );
  if (href === undefined) {
    return <>{item.asin}</>;
  }
  return (
    <Link
      external
      href={href}
      // Let the row's own click handler open the detail pane without the
      // link also firing.
      onFollow={(event) => event.stopPropagation()}
    >
      {item.asin}
    </Link>
  );
};

export const CATALOG_ISSUES_COLUMN_DEFINITIONS: ReadonlyArray<
  TableProps.ColumnDefinition<AsinWithCatalogIssues>
> = [
  {
    id: "asin",
    header: "ASIN",
    cell: (item) => <AsinSelectionCentralCell item={item} />,
    sortingField: "asin",
  },
  {
    id: "description",
    header: "Description",
    cell: (item) => item.description,
    sortingField: "description",
  },
  {
    id: "marketplace",
    header: "Marketplace",
    cell: (item) => item.marketplace,
  },
  {
    id: "domain",
    header: "Domain",
    cell: (item) => item.domain,
  },
  {
    id: "issues",
    header: "Issues",
    cell: (item) => <IssueCountBadges item={item} />,
    sortingComparator: (a, b) =>
      a.errorCount !== b.errorCount
        ? a.errorCount - b.errorCount
        : a.warningCount - b.warningCount,
  },
];

function compareRows(
  a: AsinWithCatalogIssues,
  b: AsinWithCatalogIssues,
  sortingColumn: TableProps.SortingColumn<AsinWithCatalogIssues>
): number {
  if (sortingColumn.sortingComparator) {
    return sortingColumn.sortingComparator(a, b);
  }
  const field = sortingColumn.sortingField as
    | keyof AsinWithCatalogIssues
    | undefined;
  if (field === undefined) {
    return 0;
  }
  const aValue = a[field];
  const bValue = b[field];
  if (typeof aValue === "number" && typeof bValue === "number") {
    return aValue - bValue;
  }
  return String(aValue).localeCompare(String(bValue));
}

export function sortAsinsWithIssues(
  rows: readonly AsinWithCatalogIssues[],
  sortingColumn: TableProps.SortingColumn<AsinWithCatalogIssues> | undefined,
  descending: boolean
): AsinWithCatalogIssues[] {
  const sorted = [...rows];
  if (sortingColumn === undefined) {
    return sorted;
  }
  sorted.sort((a, b) => {
    const result = compareRows(a, b, sortingColumn);
    return descending ? -result : result;
  });
  return sorted;
}

export const CatalogIssuesTable: React.FC<CatalogIssuesTableProps> = ({
  items,
  loading,
  filteringText,
  onFilteringChange,
  selectedItems,
  onSelectionChange,
}) => {
  const [sortingColumn, setSortingColumn] = useState<
    TableProps.SortingColumn<AsinWithCatalogIssues> | undefined
  >(undefined);
  const [sortingDescending, setSortingDescending] = useState(false);

  const sortedItems = sortAsinsWithIssues(
    items,
    sortingColumn,
    sortingDescending
  );

  return (
    <Table
      data-testid="catalog-issues-table"
      loading={loading}
      loadingText="Loading catalog issues"
      items={sortedItems}
      columnDefinitions={
        CATALOG_ISSUES_COLUMN_DEFINITIONS as TableProps.ColumnDefinition<AsinWithCatalogIssues>[]
      }
      trackBy={(item) => `${item.asin}/${item.obfuscatedMarketplaceId}`}
      selectionType="multi"
      selectedItems={selectedItems as AsinWithCatalogIssues[]}
      onSelectionChange={({ detail }) =>
        onSelectionChange(detail.selectedItems as AsinWithCatalogIssues[])
      }
      sortingColumn={sortingColumn}
      sortingDescending={sortingDescending}
      onSortingChange={({ detail }) => {
        setSortingColumn(detail.sortingColumn);
        setSortingDescending(detail.isDescending === true);
      }}
      filter={
        <TextFilter
          data-testid="catalog-issues-filter"
          filteringText={filteringText}
          filteringPlaceholder="Filter by ASIN or description..."
          onChange={({ detail }) => onFilteringChange(detail.filteringText)}
        />
      }
      empty={
        <Box textAlign="center" color="inherit">
          No catalog issues found
        </Box>
      }
    />
  );
};

export default CatalogIssuesTable;
